import type { OutlineEntry, VersionInfo } from '@/lib/types/report'

/** Section ids changed in the (from, to] version window, from the stored change
 *  descriptors — no text diffing needed (each section_write names its section). */
export function changedSectionIds(versions: VersionInfo[], from: number, to: number): string[] {
  if (to <= from) return []
  const ids = new Set<string>()
  for (const v of versions) {
    if (v.version > from && v.version <= to && v.change.type === 'section_write' && v.change.section_id) {
      ids.add(v.change.section_id)
    }
  }
  return [...ids]
}

export function versionLabel(v: VersionInfo): string {
  if (v.change.type === 'create') return `v${v.version} — created`
  if (v.change.type === 'section_write') return `v${v.version} — section: ${v.change.section_id ?? '?'}`
  return `v${v.version} — ${v.change.type}`
}

/** id → human title, for timeline pills and feed entries. */
export function sectionTitleMap(outline: OutlineEntry[]): Map<string, string> {
  return new Map(outline.map((o) => [o.id, o.title]))
}

/** Human label: "v1 · Created" / "v4 · Process Contributions" (raw id when untitled). */
export function versionTitleLabel(v: VersionInfo, titles: Map<string, string>): string {
  if (v.change.type === 'create') return `v${v.version} · Created`
  if (v.change.type === 'section_write') {
    const id = v.change.section_id ?? '?'
    return `v${v.version} · ${titles.get(id) ?? id}`
  }
  return `v${v.version} · ${v.change.type}`
}

/** hh:mm local time for feed entries and pill tooltips; '' when unparseable. */
export function shortTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const TIMELINE_TAIL = 5
export type TimelineItem = VersionInfo | 'gap'

/** Collapse long histories to first · … · last five; the selected version is always
 *  visible (spliced next to the gap when it falls inside the collapsed range). */
export function timelineWindow(versions: VersionInfo[], selected: number): TimelineItem[] {
  if (versions.length <= TIMELINE_TAIL + 2) return [...versions]
  const head = versions[0]
  const tail = versions.slice(-TIMELINE_TAIL)
  const items: TimelineItem[] = [head, 'gap', ...tail]
  const visible = new Set([head.version, ...tail.map((v) => v.version)])
  if (!visible.has(selected)) {
    const sel = versions.find((v) => v.version === selected)
    if (sel) items.splice(2, 0, sel, 'gap')
  }
  return items
}

/** Last write version per section, capped at `upTo` — feeds the persistent
 *  "updated · vN" chips (cap keeps scrubbed snapshots honest). */
export function lastWriteVersions(versions: VersionInfo[], upTo: number): Record<string, number> {
  const last: Record<string, number> = {}
  for (const v of versions) {
    if (v.version <= upTo && v.change.type === 'section_write' && v.change.section_id) {
      last[v.change.section_id] = Math.max(last[v.change.section_id] ?? 0, v.version)
    }
  }
  return last
}
